import React from 'react';
import {
    ProductImportBatch,
    ProductImportBatchStatus,
    ProductImportRowMessage,
    ProductImportRowStatus,
} from '../../services/productImport.service';

// ── design tokens ────────────────────────────────────────────────────────────

interface Tokens {
    teal: string;
    tealDark: string;
    tealMuted: string;
    tealLight: string;
    tealGradient: string;
    bg: string;
    bgSubtle: string;
    surface: string;
    border: string;
    borderStrong: string;
    text: string;
    textMuted: string;
    textDim: string;
    cardShadow: string;
    cardShadowHover: string;
    cardShadowElevated: string;
    radius: number;
    radiusSmall: number;
    radiusLarge: number;
}

export const TOKENS: Tokens = {
    teal: '#0F7679',
    tealDark: '#05292A',
    tealMuted: '#0D5759',
    tealLight: '#EAF4F4',
    tealGradient: 'linear-gradient(135deg, #0F7679 0%, #0D5759 100%)',
    bg: '#F4F8F9',
    bgSubtle: '#FAFCFC',
    surface: '#FFFFFF',
    border: '#E5ECEC',
    borderStrong: '#D6E0E1',
    text: '#1F2A2B',
    textMuted: '#6B7787',
    textDim: '#94A1A8',
    cardShadow: '0 1px 2px rgba(5,41,42,0.04), 0 8px 24px rgba(5,41,42,0.05)',
    cardShadowHover: '0 1px 2px rgba(5,41,42,0.06), 0 12px 28px rgba(5,41,42,0.08)',
    cardShadowElevated: '0 2px 4px rgba(5,41,42,0.06), 0 24px 48px rgba(5,41,42,0.10)',
    radius: 12,
    radiusSmall: 8,
    radiusLarge: 16,
};

interface StatusPalette {
    readyBg: string;
    readyText: string;
    readyDot: string;
    skipBg: string;
    skipText: string;
    skipDot: string;
    needsBg: string;
    needsText: string;
    needsDot: string;
    pendingBg: string;
    pendingText: string;
    pendingDot: string;
    infoBg: string;
    infoText: string;
    infoDot: string;
}

export const STATUS_PALETTE: StatusPalette = {
    readyBg: '#E6F6EE',
    readyText: '#0E8B53',
    readyDot: '#16A36A',
    skipBg: '#EEF2F7',
    skipText: '#475569',
    skipDot: '#64748B',
    needsBg: '#FDECEC',
    needsText: '#C53030',
    needsDot: '#E03D3D',
    pendingBg: '#FEF3C7',
    pendingText: '#92400E',
    pendingDot: '#D97706',
    infoBg: '#E0F2FE',
    infoText: '#075985',
    infoDot: '#0284C7',
};

// ── style helpers ────────────────────────────────────────────────────────────

export const primaryButton = (disabled?: boolean): React.CSSProperties => ({
    background: disabled ? '#A8C9CA' : TOKENS.teal,
    color: '#fff',
    border: 'none',
    borderRadius: 999,
    padding: '10px 22px',
    fontSize: 14,
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background 120ms ease, transform 120ms ease',
});

export const secondaryButton = (): React.CSSProperties => ({
    background: TOKENS.surface,
    color: TOKENS.tealDark,
    border: `1px solid ${TOKENS.borderStrong}`,
    borderRadius: 999,
    padding: '10px 22px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 120ms ease, border-color 120ms ease',
});

export const ghostButton = (): React.CSSProperties => ({
    background: 'transparent',
    color: TOKENS.teal,
    border: 'none',
    padding: '6px 12px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
});

// ── components ───────────────────────────────────────────────────────────────

export const Card: React.FC<{
    children: React.ReactNode;
    style?: React.CSSProperties;
    interactive?: boolean;
    onClick?: () => void;
}> = ({ children, style, interactive, onClick }) => {
    const [hovered, setHovered] = React.useState(false);
    return (
        <div
            onClick={onClick}
            onMouseEnter={() => interactive && setHovered(true)}
            onMouseLeave={() => interactive && setHovered(false)}
            style={{
                background: TOKENS.surface,
                border: `1px solid ${hovered ? TOKENS.borderStrong : TOKENS.border}`,
                borderRadius: TOKENS.radius,
                padding: 24,
                boxShadow: hovered ? TOKENS.cardShadowHover : TOKENS.cardShadow,
                cursor: interactive ? 'pointer' : 'default',
                transition: 'box-shadow 160ms ease, border-color 160ms ease, transform 160ms ease',
                transform: hovered && interactive ? 'translateY(-1px)' : 'none',
                ...style,
            }}
        >
            {children}
        </div>
    );
};

export const Counter: React.FC<{
    label: string;
    value: number;
    tone?: 'ready' | 'skip' | 'needs' | 'neutral';
}> = ({ label, value, tone = 'neutral' }) => {
    let bg = 'transparent';
    let color = TOKENS.text;
    let border = `1px solid ${TOKENS.border}`;
    if (tone === 'ready') {
        bg = STATUS_PALETTE.readyBg;
        color = STATUS_PALETTE.readyText;
        border = 'none';
    } else if (tone === 'skip') {
        bg = STATUS_PALETTE.skipBg;
        color = STATUS_PALETTE.skipText;
        border = 'none';
    } else if (tone === 'needs') {
        bg = STATUS_PALETTE.needsBg;
        color = STATUS_PALETTE.needsText;
        border = 'none';
    }
    return (
        <div
            style={{
                background: bg,
                border,
                borderRadius: TOKENS.radiusSmall,
                padding: '10px 16px',
                minWidth: 96,
                textAlign: 'center',
            }}
        >
            <div style={{ color: TOKENS.textMuted, fontSize: 11, fontWeight: 500, letterSpacing: 0.2 }}>
                {label.toUpperCase()}
            </div>
            <div style={{ color, fontSize: 22, fontWeight: 700, marginTop: 2 }}>{value}</div>
        </div>
    );
};

export const StatusChip: React.FC<{ status: ProductImportBatchStatus; size?: 'sm' | 'md' }> = ({
    status,
    size = 'md',
}) => {
    let bg = STATUS_PALETTE.skipBg;
    let color = STATUS_PALETTE.skipText;
    let dot = STATUS_PALETTE.skipDot;
    let label = status as string;
    if (status === 'CONFIRMED') {
        bg = STATUS_PALETTE.readyBg;
        color = STATUS_PALETTE.readyText;
        dot = STATUS_PALETTE.readyDot;
        label = 'Confirmed';
    } else if (status === 'FAILED') {
        bg = STATUS_PALETTE.needsBg;
        color = STATUS_PALETTE.needsText;
        dot = STATUS_PALETTE.needsDot;
        label = 'Failed';
    } else if (status === 'EXPIRED') {
        bg = STATUS_PALETTE.skipBg;
        color = STATUS_PALETTE.skipText;
        dot = STATUS_PALETTE.skipDot;
        label = 'Expired';
    } else if (status === 'PREVIEWED') {
        bg = STATUS_PALETTE.pendingBg;
        color = STATUS_PALETTE.pendingText;
        dot = STATUS_PALETTE.pendingDot;
        label = 'Previewed';
    } else if (status === 'CONFIRMING') {
        bg = STATUS_PALETTE.pendingBg;
        color = STATUS_PALETTE.pendingText;
        dot = STATUS_PALETTE.pendingDot;
        label = 'Confirming';
    }
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: bg,
                color,
                padding: size === 'sm' ? '2px 10px' : '4px 12px',
                borderRadius: 999,
                fontSize: size === 'sm' ? 11 : 12,
                fontWeight: 600,
            }}
        >
            <span
                style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: dot,
                    display: 'inline-block',
                }}
            />
            {label}
        </span>
    );
};

export const RowStatusChip: React.FC<{ status: ProductImportRowStatus }> = ({ status }) => {
    const map: Record<ProductImportRowStatus, { bg: string; color: string; dot: string; label: string }> = {
        READY_CREATE: {
            bg: STATUS_PALETTE.readyBg,
            color: STATUS_PALETTE.readyText,
            dot: STATUS_PALETTE.readyDot,
            label: 'Ready',
        },
        SKIP_EXISTING: {
            bg: STATUS_PALETTE.skipBg,
            color: STATUS_PALETTE.skipText,
            dot: STATUS_PALETTE.skipDot,
            label: 'Skip',
        },
        NEEDS_ATTENTION: {
            bg: STATUS_PALETTE.needsBg,
            color: STATUS_PALETTE.needsText,
            dot: STATUS_PALETTE.needsDot,
            label: 'Needs attention',
        },
    };
    const m = map[status];
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: m.bg,
                color: m.color,
                padding: '3px 10px',
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 600,
                whiteSpace: 'nowrap',
            }}
        >
            <span
                style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: m.dot,
                    display: 'inline-block',
                }}
            />
            {m.label}
        </span>
    );
};

export const MessageList: React.FC<{ messages: ProductImportRowMessage[] | null | undefined }> = ({
    messages,
}) => {
    if (!messages || messages.length === 0)
        return <span style={{ color: TOKENS.textDim }}>—</span>;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {messages.map((m, i) => {
                let dotColor = STATUS_PALETTE.infoDot;
                let textColor = TOKENS.textMuted;
                if (m.level === 'error') {
                    dotColor = STATUS_PALETTE.needsDot;
                    textColor = STATUS_PALETTE.needsText;
                } else if (m.level === 'warning') {
                    dotColor = STATUS_PALETTE.pendingDot;
                    textColor = STATUS_PALETTE.pendingText;
                }
                return (
                    <span
                        key={i}
                        style={{
                            color: textColor,
                            fontSize: 12,
                            lineHeight: 1.4,
                            display: 'inline-flex',
                            alignItems: 'flex-start',
                            gap: 8,
                        }}
                    >
                        <span
                            style={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                background: dotColor,
                                display: 'inline-block',
                                marginTop: 5,
                                flexShrink: 0,
                            }}
                        />
                        <span>
                            <strong style={{ fontWeight: 600 }}>{m.field}:</strong> {m.message}
                        </span>
                    </span>
                );
            })}
        </div>
    );
};

export const BatchSummary: React.FC<{ batch: ProductImportBatch }> = ({ batch }) => (
    <Card>
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ minWidth: 0, flex: '1 1 240px' }}>
                <div style={{ color: TOKENS.textMuted, fontSize: 11, fontWeight: 500, letterSpacing: 0.2 }}>
                    FILE
                </div>
                <div
                    style={{
                        color: TOKENS.text,
                        fontWeight: 600,
                        fontSize: 15,
                        marginTop: 4,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                    title={batch.filename ?? ''}
                >
                    {batch.filename ?? '—'}
                </div>
                <div style={{ marginTop: 8 }}>
                    <StatusChip status={batch.status} />
                </div>
            </div>
            <Counter label="Total" value={batch.totalRows} />
            <Counter label="Ready" value={batch.readyCount} tone="ready" />
            <Counter label="Skip" value={batch.skipCount} tone="skip" />
            <Counter label="Needs attention" value={batch.needsAttentionCount} tone="needs" />
        </div>
    </Card>
);

// ── small format helpers ─────────────────────────────────────────────────────

export const formatRelative = (iso: string): string => {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return iso;
    const diffMs = Date.now() - then;
    const minutes = Math.round(diffMs / 60_000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours} hr${hours === 1 ? '' : 's'} ago`;
    const days = Math.round(hours / 24);
    if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
    return new Date(iso).toLocaleDateString();
};

export const formatExactDate = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
};

// ── icons (inline SVGs, no extra deps) ───────────────────────────────────────

export const IconUploadCloud: React.FC<{ size?: number; color?: string }> = ({
    size = 48,
    color = TOKENS.teal,
}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 16l-4-4-4 4" />
        <path d="M12 12v9" />
        <path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3" />
        <path d="M16 16l-4-4-4 4" />
    </svg>
);

export const IconCheck: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = '#fff' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

export const IconFile: React.FC<{ size?: number; color?: string }> = ({ size = 18, color = TOKENS.teal }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <polyline points="14 2 14 8 20 8" />
    </svg>
);

export const IconArrowLeft: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <line x1="19" y1="12" x2="5" y2="12" />
        <polyline points="12 19 5 12 12 5" />
    </svg>
);

export const IconRefresh: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 4 23 10 17 10" />
        <polyline points="1 20 1 14 7 14" />
        <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
    </svg>
);

// ── progress stepper ─────────────────────────────────────────────────────────

export type StepperStage = 'upload' | 'preview' | 'confirm';

export const Stepper: React.FC<{ stage: StepperStage; failed?: boolean }> = ({ stage, failed }) => {
    const steps: { id: StepperStage; label: string }[] = [
        { id: 'upload', label: 'Upload' },
        { id: 'preview', label: 'Preview' },
        { id: 'confirm', label: failed ? 'Failed' : 'Confirm' },
    ];
    const stageIndex = steps.findIndex((s) => s.id === stage);

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 0,
                padding: '14px 20px',
                background: TOKENS.surface,
                border: `1px solid ${TOKENS.border}`,
                borderRadius: 999,
                boxShadow: TOKENS.cardShadow,
                width: 'fit-content',
            }}
        >
            {steps.map((s, idx) => {
                const isActive = idx === stageIndex;
                const isComplete = idx < stageIndex;
                const isFailed = failed && idx === 2;

                let circleBg = TOKENS.bg;
                let circleBorder = TOKENS.borderStrong;
                let circleColor = TOKENS.textDim;
                let labelColor = TOKENS.textDim;

                if (isComplete) {
                    circleBg = TOKENS.teal;
                    circleBorder = TOKENS.teal;
                    circleColor = '#fff';
                    labelColor = TOKENS.tealDark;
                } else if (isFailed) {
                    circleBg = STATUS_PALETTE.needsBg;
                    circleBorder = STATUS_PALETTE.needsDot;
                    circleColor = STATUS_PALETTE.needsText;
                    labelColor = STATUS_PALETTE.needsText;
                } else if (isActive) {
                    circleBg = TOKENS.surface;
                    circleBorder = TOKENS.teal;
                    circleColor = TOKENS.teal;
                    labelColor = TOKENS.tealDark;
                }

                return (
                    <React.Fragment key={s.id}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div
                                style={{
                                    width: 26,
                                    height: 26,
                                    borderRadius: '50%',
                                    background: circleBg,
                                    border: `2px solid ${circleBorder}`,
                                    color: circleColor,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    boxShadow: isActive && !isFailed ? `0 0 0 4px ${TOKENS.tealLight}` : 'none',
                                    transition: 'all 200ms ease',
                                }}
                            >
                                {isComplete ? <IconCheck size={14} /> : idx + 1}
                            </div>
                            <span style={{ color: labelColor, fontSize: 13, fontWeight: 600 }}>{s.label}</span>
                        </div>
                        {idx < steps.length - 1 && (
                            <div
                                style={{
                                    width: 40,
                                    height: 2,
                                    background: idx < stageIndex ? TOKENS.teal : TOKENS.border,
                                    margin: '0 14px',
                                    transition: 'background 200ms ease',
                                }}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};
